import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Search,
  Trash2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import {
  useCategoriesList,
  useDeleteCategory,
  type CategoryOption,
} from "@/lib/queries/categories"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Categories (list)
// LEGACY: apps/admin/src/pages/Category.jsx
// API:    POST /admin/list_category    (server pagination/sort/search)
//         POST /admin/delete_category  (delete)
//
// LIST request body:
//   page, limit, search, sortyBy?, sortDirection?    (sortyBy typo PRESERVED)
//
// LIST columns rendered (same set as legacy):
//   name        → "Name" (sortable)
//   cover_image → "Cover" (img 150px in legacy; rendered slightly smaller here)
//   order       → "Order" (sortable)
//   _id (act)   → Update link → /categories/:id (was /updateCategory/:id)
//   _id (act)   → Delete button → POST /admin/delete_category {id}
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

export default function Categories() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const categoriesQ = useCategoriesList(params)
  const deleteMut = useDeleteCategory()

  const [deleteCat, setDeleteCat] = useState<CategoryOption | null>(null)

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = categoriesQ.data?.totalPages ?? 0
  const totalDocs = categoriesQ.data?.totalDocs ?? 0

  const handleDeleteConfirm = async () => {
    if (!deleteCat) return
    await deleteMut.mutateAsync(deleteCat._id)
    setDeleteCat(null)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Categories</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} total`
                : "Group albums into categories"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search categories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search categories"
              />
            </div>
            <ExportButton<CategoryOption>
              filenamePrefix="categories"
              totalEstimate={totalDocs}
              columns={[
                { label: "Name", get: (c) => c.name },
                { label: "Order", get: (c) => c.order },
                { label: "Cover Image", get: (c) => c.cover_image },
                { label: "ID", get: (c) => c._id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/list_category", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
            <Button asChild>
              <Link to="/categories/new">Add category</Link>
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {categoriesQ.isError && (
            <div
              role="alert"
              className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load categories. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Cover</TableHead>
                <SortableTH field="name" label="Name" {...{ onSortClick, SortIcon }} />
                <SortableTH
                  field="order"
                  label="Order"
                  className="w-20"
                  {...{ onSortClick, SortIcon }}
                />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categoriesQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!categoriesQ.isLoading && (categoriesQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                    No categories match your filters.
                  </TableCell>
                </TableRow>
              )}
              {categoriesQ.data?.docs.map((c) => (
                <TableRow key={c._id}>
                  <TableCell>
                    {c.cover_image ? (
                      <img
                        src={c.cover_image}
                        alt=""
                        loading="lazy"
                        className="h-16 w-16 rounded object-cover"
                      />
                    ) : (
                      <div className="grid h-16 w-16 place-items-center rounded bg-muted text-xs text-muted-foreground">
                        no img
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-medium">
                    {c.name || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {/* CategoryOption type doesn't include `order` directly;
                        the list endpoint returns it though. Cast safely. */}
                    {(() => {
                      const o = (c as { order?: number }).order
                      return typeof o === "number" ? o : "—"
                    })()}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    <Button asChild variant="ghost" size="sm" aria-label="Edit category">
                      <Link to={`/categories/${c._id}`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteCat(c)}
                      aria-label="Delete category"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog
        open={!!deleteCat}
        onOpenChange={(open) => !open && setDeleteCat(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete category?</DialogTitle>
            <DialogDescription>
              {deleteCat?.name || "This category"} will be removed. Albums
              referencing it may show as uncategorized. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteCat(null)}
              disabled={deleteMut.isPending}
            >
              Keep category
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: React.ReactNode
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
